import React from 'react';
import { Link } from 'react-router-dom';
import { theme, responsiveContainer } from '../styles/theme';
import ByteBotLogo from './ByteBotLogo';

function Footer() {
  const currentYear = new Date().getFullYear();

  const styles = {
    footer: {
      backgroundColor: theme.colors.secondary,
      color: '#fff',
      padding: `${theme.spacing['4xl']} ${theme.spacing.lg} ${theme.spacing.xl} ${theme.spacing.lg}`,
      marginTop: 'auto',
    },
    footerContent: {
      ...responsiveContainer,
    },
    footerGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: theme.spacing['2xl'],
      marginBottom: theme.spacing['2xl'],
    },
    footerSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.md,
    },
    footerTitle: {
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.primary,
      marginBottom: theme.spacing.md,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    footerLink: {
      color: '#ccc',
      fontSize: theme.typography.fontSizes.base,
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'color 0.2s ease',
      padding: `${theme.spacing.xs} 0`,
    },
    footerText: {
      color: '#ccc',
      fontSize: theme.typography.fontSizes.base,
      lineHeight: '1.6',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: '#fff',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      marginBottom: theme.spacing.md,
      textDecoration: 'none',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    logoIcon: {
      marginRight: theme.spacing.sm,
      display: 'flex',
      alignItems: 'center',
    },
    logoAccent: {
      color: theme.colors.primary,
    },
    socialLinks: {
      display: 'flex',
      gap: theme.spacing.lg,
      marginTop: theme.spacing.lg,
    },
    socialLink: {
      color: theme.colors.primary,
      fontSize: theme.typography.fontSizes.base,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      fontWeight: theme.typography.fontWeights.bold,
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      padding: theme.spacing.sm,
      borderRadius: theme.borderRadius.md,
      border: `1px solid ${theme.colors.primary}`,
    },
    footerBottom: {
      borderTop: '1px solid #555',
      paddingTop: theme.spacing.xl,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing.lg,
    },
    copyright: {
      color: '#999',
      fontSize: theme.typography.fontSizes.sm,
    },
    legalLinks: {
      display: 'flex',
      gap: theme.spacing.xl,
      flexWrap: 'wrap',
    },
    legalLink: {
      color: '#999',
      fontSize: theme.typography.fontSizes.sm,
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'color 0.2s ease',
    },
  };

  return (
    <footer style={styles.footer}>
      <div style={styles.footerContent}>
        <div style={styles.footerGrid}>
          {/* Company Info */}
          <div style={styles.footerSection}>
            <Link to="/" style={styles.logo}>
              <div style={styles.logoIcon}>
                <ByteBotLogo 
                  size={32} 
                  color="#fff"
                />
              </div>
              [BYTE<span style={styles.logoAccent}>BOT</span>]
            </Link>
            <p style={styles.footerText}>
              Making AI chatbots accessible to every business. 
              No coding required, no AI expertise needed. 
              Just results.
            </p>
            <div style={styles.socialLinks}>
              <a 
                href="mailto:service@bytesizeds.com"
                style={styles.socialLink}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = `${theme.colors.primary}20`;
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                [EMAIL]
              </a>
              <a 
                href="https://bytesizeds.com"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.socialLink}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = `${theme.colors.primary}20`;
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                [BYTESIZEDS]
              </a>
            </div>
          </div>

          {/* Product */}
          <div style={styles.footerSection}>
            <h3 style={styles.footerTitle}>Product</h3>
            <Link 
              to="/features" 
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#ccc'}
            >
              Features
            </Link>
            <Link 
              to="/pricing" 
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#ccc'}
            >
              Pricing
            </Link>
            {/* Hidden for MVP - will add back later
            <span 
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#ccc'}
            >
              ByteBot Builder
            </span>
            <span 
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#ccc'}
            >
              Integrations
            </span>
            */}
          </div>

          {/* Company */}
          <div style={styles.footerSection}>
            <h3 style={styles.footerTitle}>Company</h3>
            <Link 
              to="/about" 
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#ccc'}
            >
              About Us
            </Link>
            {/* Hidden for MVP - will add back later
            <Link 
              to="/blog" 
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#ccc'}
            >
              Blog
            </Link>
            */}
            <a 
              href="https://bytesizeds.com"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#ccc'}
            >
              ByteSizeDS.com
            </a>
          </div>

          {/* Support */}
          <div style={styles.footerSection}>
            <h3 style={styles.footerTitle}>Support</h3>
            <Link 
              to="/contact" 
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#ccc'}
            >
              Contact Us
            </Link>
            {/* Hidden for MVP - will add back later
            <span 
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#ccc'}
            >
              Help Center
            </span>
            <span 
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#ccc'}
            >
              Documentation
            </span>
            <span 
              style={styles.footerLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#ccc'}
            >
              Status
            </span>
            */}
          </div>
        </div>

        <div style={styles.footerBottom}>
          <div style={styles.copyright}>
            © {currentYear} ByteBot by <a href="https://bytesizeds.com" target="_blank" rel="noopener noreferrer" style={{color: theme.colors.primary, textDecoration: 'none'}}>ByteSizeDS.com</a>. All rights reserved.
          </div>
          <div style={styles.legalLinks}>
            <Link 
              to="/privacy" 
              style={styles.legalLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#999'}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              style={styles.legalLink}
              onMouseEnter={(e) => e.target.style.color = theme.colors.primary}
              onMouseLeave={(e) => e.target.style.color = '#999'}
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer; 